import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  SignOut,
  MagnifyingGlass,
  FileCsv,
  FileXls,
  ArrowClockwise,
  CaretDown,
  CaretRight,
  Users,
  Sparkle,
  Handshake,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import {
  fetchStats,
  fetchList,
  updateItem,
  exportData,
  clearToken,
  formatApiErrorDetail,
} from "@/lib/adminApi";

const STATUSES = ["New", "Under Review", "Contacted", "Qualified", "Closed"];
const STATUS_STYLE = {
  New: "bg-altin-gold/15 text-altin-goldHover border-altin-gold/40",
  "Under Review": "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Qualified: "bg-green-50 text-green-700 border-green-200",
  Closed: "bg-neutral-100 text-neutral-500 border-neutral-300",
};
const INDUSTRIES = [
  "Food & Agriculture", "Construction Materials", "Industrial Equipment & Machinery",
  "Manufacturing", "Consumer Goods", "Chemicals & Raw Materials", "Energy",
  "Technology & Digital Solutions", "Healthcare & Medical Supplies",
  "Logistics & Supply Chain", "Investment & Business Development",
];
const TYPES = ["Supplier", "Distributor", "Manufacturer", "Investor", "Buyer", "Strategic Partner", "Technology Partner", "Logistics Partner", "Medical Supplier", "Other"];

const emptyFilters = {
  search: "", industry: "", partnership_type: "", country: "",
  status: "", date_from: "", date_to: "",
};

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-altin-border p-6">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-altin-muted">{label}</div>
        <Icon size={18} weight="light" className="text-altin-gold" />
      </div>
      <div className="mt-4 font-serif-display text-4xl text-altin-ink">{value}</div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(d);
  }
}

export default function AdminDashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("partnerships");
  const [filters, setFilters] = useState(emptyFilters);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401) {
      toast.error("Session expired. Please log in again.");
      clearToken();
      onLogout();
      return true;
    }
    return false;
  }, [onLogout]);

  const loadStats = useCallback(async () => {
    try {
      setStats(await fetchStats());
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to load statistics.");
    }
  }, [handleAuthError]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "contacts"
        ? { search: filters.search, status: filters.status, date_from: filters.date_from, date_to: filters.date_to }
        : filters;
      setRows(await fetchList(tab, params));
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [tab, filters, handleAuthError]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRows(); }, [tab]);

  const applyFilters = () => loadRows();
  const clearFilters = () => {
    setFilters(emptyFilters);
    setLoading(true);
    fetchList(tab, {})
      .then(setRows)
      .catch((err) => {
        if (!handleAuthError(err)) toast.error("Failed to load submissions.");
      })
      .finally(() => setLoading(false));
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateItem(tab, id, { status });
      setRows((r) => r.map((x) => (x.id === id ? updated : x)));
      toast.success(`Marked as ${status}.`);
      loadStats();
    } catch (err) {
      if (!handleAuthError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveNote = async (id) => {
    try {
      const updated = await updateItem(tab, id, { notes: noteDraft });
      setRows((r) => r.map((x) => (x.id === id ? updated : x)));
      toast.success("Note saved.");
    } catch (err) {
      if (!handleAuthError(err)) toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doExport = async (fmt) => {
    try {
      await exportData(tab, fmt);
      toast.success(`Exported ${tab} as ${fmt.toUpperCase()}.`);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Export failed.");
    }
  };

  const isPartner = tab === "partnerships";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-altin-bg">
      {/* Top bar */}
      <header className="bg-altin-ink text-white sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/altin-logo-sm.png" alt="Altin Link" className="h-11 w-auto object-contain" />
            <div className="hidden sm:block border-l border-white/20 pl-4">
              <div className="font-serif-display text-xl tracking-tight">Lead Dashboard</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-altin-gold">{admin?.email}</div>
            </div>
          </div>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 border border-white/25 px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:border-altin-gold hover:text-altin-gold transition-colors"
          >
            <SignOut size={16} weight="light" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-total" icon={Users} label="Total Leads" value={stats?.total_leads ?? "—"} />
          <StatCard testid="stat-new" icon={Sparkle} label="New Leads" value={stats?.new_leads ?? "—"} />
          <StatCard testid="stat-partnerships" icon={Handshake} label="Partnership Requests" value={stats?.partnership_requests ?? "—"} />
          <StatCard testid="stat-contacts" icon={EnvelopeSimple} label="Contact Requests" value={stats?.contact_requests ?? "—"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <BreakdownPanel testid="breakdown-industry" title="Leads by Industry" data={stats?.leads_by_industry} />
          <BreakdownPanel testid="breakdown-country" title="Leads by Country" data={stats?.leads_by_country} />
        </div>

        {/* Tabs + export */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-b border-altin-border">
          <div className="flex">
            {[["partnerships", "Partnership Applications"], ["contacts", "Contact Submissions"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`tab-${k}`}
                onClick={() => { setTab(k); setFilters(emptyFilters); setExpanded(null); }}
                className={`px-6 py-4 text-xs uppercase tracking-[0.18em] border-b-2 -mb-px transition-colors ${
                  tab === k ? "border-altin-gold text-altin-ink" : "border-transparent text-altin-muted hover:text-altin-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-3">
            <button data-testid="export-csv" onClick={() => doExport("csv")} className="inline-flex items-center gap-2 border border-altin-border px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-altin-ink hover:border-altin-gold hover:text-altin-gold transition-colors">
              <FileCsv size={16} weight="light" /> CSV
            </button>
            <button data-testid="export-xlsx" onClick={() => doExport("xlsx")} className="inline-flex items-center gap-2 border border-altin-border px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-altin-ink hover:border-altin-gold hover:text-altin-gold transition-colors">
              <FileXls size={16} weight="light" /> Excel
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white border border-altin-border p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2 relative">
            <MagnifyingGlass size={16} weight="light" className="absolute left-3 top-1/2 -translate-y-1/2 text-altin-muted" />
            <input
              data-testid="filter-search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder={isPartner ? "Search company or contact person..." : "Search company or name..."}
              className="w-full bg-altin-bg border border-altin-border focus:border-altin-gold focus:outline-none pl-9 pr-3 py-2.5 text-sm text-altin-ink"
            />
          </div>
          <FilterSelect testid="filter-status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUSES} placeholder="All statuses" />
          {isPartner && (
            <FilterSelect testid="filter-industry" value={filters.industry} onChange={(v) => setFilters({ ...filters, industry: v })} options={INDUSTRIES} placeholder="All industries" />
          )}
          {isPartner && (
            <FilterSelect testid="filter-type" value={filters.partnership_type} onChange={(v) => setFilters({ ...filters, partnership_type: v })} options={TYPES} placeholder="All partnership types" />
          )}
          {isPartner && (
            <input
              data-testid="filter-country"
              value={filters.country}
              onChange={(e) => setFilters({ ...filters, country: e.target.value })}
              placeholder="Country"
              className="bg-altin-bg border border-altin-border focus:border-altin-gold focus:outline-none px-3 py-2.5 text-sm text-altin-ink"
            />
          )}
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-altin-muted mb-1">From</label>
            <input type="date" data-testid="filter-date-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-full bg-altin-bg border border-altin-border focus:border-altin-gold focus:outline-none px-3 py-2 text-sm text-altin-ink" />
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.2em] text-altin-muted mb-1">To</label>
            <input type="date" data-testid="filter-date-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-full bg-altin-bg border border-altin-border focus:border-altin-gold focus:outline-none px-3 py-2 text-sm text-altin-ink" />
          </div>
          <div className="flex items-end gap-2">
            <button data-testid="filter-apply" onClick={applyFilters} className="flex-1 bg-altin-ink text-white px-4 py-2.5 text-xs uppercase tracking-[0.18em] hover:bg-altin-gold transition-colors">Apply</button>
            <button data-testid="filter-clear" onClick={clearFilters} className="px-3 py-2.5 border border-altin-border text-altin-muted hover:border-altin-gold hover:text-altin-gold transition-colors" aria-label="Reset filters">
              <ArrowClockwise size={16} weight="light" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="mt-4 text-xs uppercase tracking-[0.2em] text-altin-muted">
          {loading ? "Loading..." : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
        </div>

        <div className="mt-3 bg-white border border-altin-border divide-y divide-altin-border" data-testid="admin-table">
          {rows.length === 0 && !loading && (
            <div className="p-10 text-center text-altin-muted">No submissions match your filters.</div>
          )}
          {rows.map((row) => {
            const open = expanded === row.id;
            return (
              <div key={row.id} data-testid={`row-${row.id}`}>
                <div className="grid grid-cols-12 items-center gap-4 px-5 py-4">
                  <button
                    onClick={() => { setExpanded(open ? null : row.id); setNoteDraft(row.notes || ""); }}
                    className="col-span-12 md:col-span-5 flex items-center gap-3 text-left"
                    data-testid={`row-toggle-${row.id}`}
                  >
                    {open ? <CaretDown size={16} weight="light" className="text-altin-gold shrink-0" /> : <CaretRight size={16} weight="light" className="text-altin-muted shrink-0" />}
                    <span>
                      <span className="block font-serif-display text-lg text-altin-ink leading-tight">
                        {isPartner ? row.company_name : (row.company || row.name)}
                      </span>
                      <span className="block text-xs text-altin-muted">
                        {isPartner ? row.contact_person : row.name} · {row.email}
                      </span>
                    </span>
                  </button>

                  <div className="col-span-6 md:col-span-3 text-xs text-altin-muted">
                    {isPartner && <div>{row.industry || "—"}</div>}
                    {isPartner && <div className="text-altin-ink">{row.partnership_type || "—"}</div>}
                    {!isPartner && <div>{row.subject || "—"}</div>}
                  </div>

                  <div className="col-span-3 md:col-span-2 text-xs text-altin-muted">
                    {isPartner ? (row.country || "—") : ""}
                    <div>{fmtDate(row.created_at)}</div>
                  </div>

                  <div className="col-span-3 md:col-span-2 flex justify-end">
                    <select
                      data-testid={`status-select-${row.id}`}
                      value={row.status || "New"}
                      onChange={(e) => changeStatus(row.id, e.target.value)}
                      className={`text-[11px] uppercase tracking-wide border px-2 py-1.5 rounded-sm cursor-pointer ${STATUS_STYLE[row.status] || STATUS_STYLE.New}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                {open && (
                  <div className="px-5 pb-6 bg-altin-bg/50" data-testid={`row-detail-${row.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 pt-4 text-sm">
                      {isPartner ? (
                        <>
                          <Info label="Position" value={row.position} />
                          <Info label="Website" value={row.company_website} />
                          <Info label="Phone" value={row.phone} />
                          <Info label="Country" value={row.country} />
                          <div className="md:col-span-2"><Info label="Proposal" value={row.description} /></div>
                        </>
                      ) : (
                        <>
                          <Info label="Phone" value={row.phone} />
                          <Info label="Subject" value={row.subject} />
                          <div className="md:col-span-2"><Info label="Message" value={row.message} /></div>
                        </>
                      )}
                    </div>
                    <div className="mt-5">
                      <label className="block text-[10px] uppercase tracking-[0.25em] text-altin-gold mb-2">Internal Notes</label>
                      <textarea
                        data-testid={`notes-${row.id}`}
                        rows={2}
                        value={expanded === row.id ? noteDraft : (row.notes || "")}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        className="w-full bg-white border border-altin-border focus:border-altin-gold focus:outline-none p-3 text-sm text-altin-ink"
                        placeholder="Add an internal note..."
                      />
                      <button
                        data-testid={`save-note-${row.id}`}
                        onClick={() => saveNote(row.id)}
                        className="mt-2 bg-altin-ink text-white px-5 py-2 text-xs uppercase tracking-[0.18em] hover:bg-altin-gold transition-colors"
                      >
                        Save Note
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function BreakdownPanel({ title, data, testid }) {
  const max = Math.max(1, ...(data || []).map((d) => d.count));
  return (
    <div data-testid={testid} className="bg-white border border-altin-border p-6">
      <div className="text-[10px] uppercase tracking-[0.22em] text-altin-muted mb-5">{title}</div>
      {(!data || data.length === 0) && <div className="text-sm text-altin-muted">No data yet.</div>}
      <div className="space-y-3">
        {(data || []).slice(0, 6).map((d) => (
          <div key={d.label}>
            <div className="flex items-center justify-between text-sm text-altin-ink mb-1">
              <span>{d.label}</span>
              <span className="text-altin-muted">{d.count}</span>
            </div>
            <div className="h-1.5 bg-altin-surfaceAlt">
              <div className="h-full bg-altin-gold" style={{ width: `${(d.count / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-altin-bg border border-altin-border focus:border-altin-gold focus:outline-none px-3 py-2.5 text-sm cursor-pointer ${value ? "text-altin-ink" : "text-altin-muted"}`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o} className="text-altin-ink">{o}</option>)}
    </select>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-altin-muted mb-1">{label}</div>
      <div className="text-altin-ink break-words">{value || "—"}</div>
    </div>
  );
}
